import { FilePdfOutlined } from "@ant-design/icons";
import { Button, DatePicker, Divider, Flex, Switch, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { AppointmentStatus, PastAndFutureAppointmentsArgs, PastAndFutureAppointments__PastAndFutureAppointments__AppointmentsQueryQuery } from "@/__graphql__/graphql";
import downloadAttachment from "@/functions/download-attachment";
import filterUnique from "@/functions/filter-unique";
import formatAddress from "@/functions/format-address";
import tableSorter from "@/functions/table-sorter";
import truncate from "@/functions/truncate";
import useRelationProfile from "@/hooks/use-relation-profile";

import { useAppointments } from "./useAppointments";

const PAGE_SIZE = 10;
type Element = PastAndFutureAppointments__PastAndFutureAppointments__AppointmentsQueryQuery["pastAndFutureAppointments"]["edges"][number];

const DEFAULT_FILTERS: PastAndFutureAppointmentsArgs = {
  endDate: undefined,
  locationIds: undefined,
  status: undefined,
  startDate: undefined,
  completed: undefined,
};

function collectRelations(relations: Array<Element["location"]["relation"]>) {
  const filterOptions = relations.map(r => ({
    value: r.id,
    text: `(${r.afasCode}) ${r.name}`,
  }));

  return [...filterUnique(filterOptions)].sort(tableSorter);
}

function collectLocations(locationMap: Record<string, unknown>, relationId: string | null) {
  // eslint-disable-next-line prettier/prettier
  const baseElements = relationId ? [locationMap[relationId]] : Object.values(locationMap);

  return baseElements.map(element => {
    return {
      text: element.name,
      value: element.name,
      children: element.locations.map(l => ({
        value: l.id,
        text: `${l.name} (${formatAddress(l.address as any)})`,
      })),
    };
  });
}

export default function ListAppointments() {
  const [offset, setOffset] = useState(0);

  const [relationId, setRelationId] = useState<string | null>(null);
  const [filters, setFilters] = useState<PastAndFutureAppointmentsArgs>(DEFAULT_FILTERS);

  const { isDealer, locationMap, relationList } = useRelationProfile();
  const [{ data, fetching: loading }] = useAppointments({ ...filters, offset });

  const columns: ColumnsType<Element> = useMemo(() => {
    const baseColumns: ColumnsType<Element> = [
      {
        title: "Datum",
        dataIndex: ["startTime"],
        key: "date",
        render: startTime => startTime.format("DD/MM/YYYY"),
      },
      {
        title: "Locatie",
        dataIndex: ["location"],
        filters: collectLocations(locationMap, relationId),
        onFilter: (value, record) => record.location.id === value,
        filterSearch: true,
        render: location => (
          <>
            <span style={{ display: "block" }}>{location.name}</span>
            <em style={{ display: "block", marginTop: 4 }}>{truncate(formatAddress(location.address), 75)}</em>
          </>
        ),
      },
      {
        title: "Soort afspraak",
        dataIndex: ["appointmentType", "publicName"],
        render: publicName => publicName ?? "-",
      },
      {
        title: "Status",
        dataIndex: ["status"],
        render: status => (status === "STATUS_SCHEDULED" ? "Ingeroosterd" : "Bevestigd"),
        filters: [
          { text: "Ingeroosterd", value: AppointmentStatus.StatusScheduled },
          { text: "Bevestigd", value: AppointmentStatus.StatusConfirmed },
        ],
      },
      {
        title: "Service medewerker",
        key: "employees",
        render: employees => employees.map(x => x.username).join(", "),
        dataIndex: ["employees"],
      },
      {
        title: "Rapportages",
        dataIndex: ["files"],
        render: files => (files.length > 0 ? <Button icon={<FilePdfOutlined />}>Bekijk rapportage</Button> : "Nog geen rapportage"),
        width: 1, // minimizes width
      },
    ];

    if (true === isDealer) {
      baseColumns.splice(1, 0, {
        title: "Klant",
        key: "relation",
        dataIndex: ["location", "relation"],
        filters: collectRelations(relationList),
        filterMultiple: false,
        onFilter: (value, record) => record.location.relation.id === value,
        render: relation => `(${relation.afasCode}) ${relation.name}`,
      });
    }

    return baseColumns;
  }, [relationId, locationMap, relationList, isDealer]);

  const preprocessedData = useMemo(
    () =>
      (data?.pastAndFutureAppointments.edges ?? []).map(appointment => ({
        ...appointment,
        startTime: dayjs(appointment.startTime),
      })),
    [data]
  );

  const handleOnClick = (worksheetId: string | null) => {
    if (null === worksheetId) return;

    const worksheet = data?.pastAndFutureAppointments.edges.find(w => w.worksheetId === worksheetId);
    if (undefined === worksheet) return;

    const fileId = worksheet.files?.[0]?.id;
    if (undefined === fileId) return;

    downloadAttachment(`file/${fileId}`);
  };

  return (
    <Table
      bordered
      className="table-clickable-rows"
      columns={columns}
      dataSource={preprocessedData}
      onChange={(pagination, filters) => {
        setOffset(((pagination.current ?? 1) - 1) * (pagination.pageSize ?? PAGE_SIZE));
        setRelationId((filters.relation?.[0] as string) || null);

        setFilters(current => ({
          ...current,
          locationIds: (filters.location as string[]) ?? undefined,
          status: (filters.status?.[0] ?? undefined) as AppointmentStatus | undefined,
        }));
      }}
      onRow={record => ({ onClick: () => handleOnClick(record.worksheetId) })}
      loading={loading}
      rowKey={record => `${record.appointmentId}+${record.worksheetId}`}
      pagination={{ pageSize: PAGE_SIZE, total: data?.pastAndFutureAppointments.totalCount ?? 0 }}
      title={_ => (
        <Flex orientation="horizontal" gap="small" align="center">
          <DatePicker.RangePicker
            allowClear
            value={[filters.startDate, filters.endDate]}
            onChange={values => {
              if (values === null || values[0] === null || values[1] === null) {
                setFilters({ ...filters, startDate: undefined, endDate: undefined });
              } else {
                setFilters({ ...filters, startDate: values[0], endDate: values[1] });
              }
            }}
          />
          <Divider orientation="vertical" />
          <Switch
            checked={filters.completed === true}
            onChange={checked => setFilters({ ...filters, completed: checked === true ? true : undefined })}
          />
          <Typography>Voltooid</Typography>
        </Flex>
      )}
    />
  );
}
